import React, { useEffect, useState } from 'react';

import { Checkpoint, CheckpointPass } from '../../../../../../types/common.types';

import useToaster from '../../../../../../hooks/use-toaster/useToaster';

import { ARCHIVED, ERROR } from '../../../../../../utils/constants';
import { generateUUID, getClassNames } from '../../../../../../utils/util';

import Button from '../../../../../common/button/Button';
import TextInput from '../../../../../common/input/text-input/TextInput';
import TimeInput from '../../../../../common/input/time-input/TimeInput';
import Modal from '../../../../../common/modal/Modal';
import AccountabilityConfigurePassesModal from '../accountability-configure-passes-modal/AccountabilityConfigurePassesModal';

import { ReactComponent as EditListIcon } from '../../../../../../resources/icons/edit-list.svg';

import './accountability-configure-checkpoint-modal.scss';

type Props = {
  status: string;
  show: boolean;
  close: () => void;
  checkpoint?: Checkpoint;
  updateCheckpoint: (checkpoint: Checkpoint) => void;
};

const AccountabilityConfigureCheckpointModal = ({ status, show, close, checkpoint, updateCheckpoint }: Props) => {
  const [form, setForm] = useState<Checkpoint>();
  const [numberOfPasses, setNumberOfPasses] = useState('');
  const [showPassesModal, setShowPassesModal] = useState(false);

  const { showToaster } = useToaster();

  useEffect(() => {
    if (checkpoint) {
      setForm(structuredClone(checkpoint));

      const numberOfPasses = checkpoint.checkpointPasses?.length;
      setNumberOfPasses(numberOfPasses ? numberOfPasses.toString() : '');
    } else {
      setForm({
        uuid: generateUUID(),
        isNew: true,
        name: '',
        deviceId: '',
        manualStartTime: false,
        multiplePassesConfigured: false,
        checkpointPasses: []
      });
      setNumberOfPasses('');
    }
  }, [checkpoint, show]);

  if (!form) return null;

  const handleFormChange = (key: keyof Checkpoint, value: typeof form[typeof key]) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [key]: value
      };
    });
  };

  const handleConfigurePassesClick = () => {
    if (!numberOfPasses || isNaN(Number(numberOfPasses)) || Number(numberOfPasses) <= 0) {
      showToaster(ERROR, 'Please enter a valid number of passes');
      return;
    }

    if (form.checkpointPasses.length !== Number(numberOfPasses)) {
      const passes = Array.from({ length: Number(numberOfPasses) }, (_, index) => ({
        name: `Lap ${index + 1}`,
        index: index + 1
      }));

      handleFormChange('checkpointPasses', passes);
    }

    setShowPassesModal(true);
  };

  const closeModal = () => {
    close();
    setForm(undefined);
  };

  const handleSaveClick = () => {
    let checkpointToSave = form;
    if (
      (form.multiplePassesConfigured && form.checkpointPasses.length === 0) ||
      form.checkpointPasses.length !== Number(numberOfPasses)
    ) {
      const checkpointPasses: CheckpointPass[] = Array.from({ length: Number(numberOfPasses) }, (_, index) => ({
        name: `Lap ${index + 1}`,
        index: index + 1
      }));

      checkpointToSave = { ...form, checkpointPasses };
    }

    updateCheckpoint(checkpointToSave);
    closeModal();
  };

  return (
    <>
      <Modal show={show && !showPassesModal}>
        <div className="accountability-configure-checkpoint-modal">
          <div className="accountability-configure-checkpoint-modal__header">Configure Checkpoint</div>
          <div
            className={getClassNames('accountability-configure-checkpoint-modal__content', {
              scroll: !form.manualStartTime
            })}
          >
            <div className="accountability-configure-checkpoint-modal__input">
              <label htmlFor="checkpoint-name" className="accountability-configure-checkpoint-modal__label">
                Checkpoint Name
              </label>
              <TextInput
                value={form.name}
                onChange={({ target }) => handleFormChange('name', target.value)}
                name="checkpoint-name"
                disabled={status === ARCHIVED}
              />
            </div>

            <div className="accountability-configure-checkpoint-modal__inline-input-container">
              <div className="accountability-configure-checkpoint-modal__input">
                <label htmlFor="device-id" className="accountability-configure-checkpoint-modal__label">
                  Device ID
                </label>
                <TextInput
                  value={form.deviceId ?? ''}
                  onChange={({ target }) => handleFormChange('deviceId', target.value)}
                  name="device-id"
                  disabled={status === ARCHIVED}
                />
              </div>

              <div className="accountability-configure-checkpoint-modal__input">
                <label htmlFor="index" className="accountability-configure-checkpoint-modal__label">
                  Checkpoint Order (Index)
                </label>
                <TextInput
                  value={form.index ?? ''}
                  onChange={({ target }) => handleFormChange('index', target.value)}
                  name="index"
                  disabled={status === ARCHIVED}
                  numberOnly
                />
              </div>
            </div>

            <div className="accountability-configure-checkpoint-modal__toggle-container">
              <label className="accountability-configure-checkpoint-modal__label">Use Manual Start-Time?</label>
              <div className="accountability-configure-checkpoint-modal__toggle">
                <button
                  className={getClassNames('accountability-configure-checkpoint-modal__toggle__button', {
                    active: !form.manualStartTime
                  })}
                  onClick={() => handleFormChange('manualStartTime', false)}
                  disabled={status === ARCHIVED}
                >
                  No
                </button>
                <button
                  className={getClassNames('accountability-configure-checkpoint-modal__toggle__button', {
                    active: form.manualStartTime
                  })}
                  onClick={() => handleFormChange('manualStartTime', true)}
                  disabled={status === ARCHIVED}
                >
                  Yes
                </button>
              </div>
            </div>

            {form.manualStartTime ? (
              <div className="accountability-configure-checkpoint-modal__start-time">
                <label htmlFor="start-time" className="accountability-configure-checkpoint-modal__label">
                  Start Time
                </label>
                <TimeInput
                  value={form.startTime}
                  onChange={(value) => handleFormChange('startTime', value)}
                  disabled={status === ARCHIVED}
                />
              </div>
            ) : (
              <>
                <div className="accountability-configure-checkpoint-modal__input-group">
                  <p>Scoring</p>
                  <div className="accountability-configure-checkpoint-modal__inline-input">
                    <label htmlFor="base-points" className="accountability-configure-checkpoint-modal__label">
                      Base Points (Checkpoint Reached)
                    </label>
                    <TextInput
                      value={form.basePoints ?? ''}
                      onChange={({ target }) => handleFormChange('basePoints', target.value)}
                      name="base-points"
                      disabled={status === ARCHIVED}
                      numberOnly
                    />
                  </div>
                  <div className="accountability-configure-checkpoint-modal__inline-input">
                    <label htmlFor="target-time" className="accountability-configure-checkpoint-modal__label">
                      Target Time (Minutes)
                    </label>
                    <TextInput
                      value={form.targetTimeInMinutes ?? ''}
                      onChange={({ target }) => handleFormChange('targetTimeInMinutes', target.value)}
                      name="target-time"
                      disabled={status === ARCHIVED}
                      numberOnly
                    />
                  </div>
                  <div className="accountability-configure-checkpoint-modal__inline-input">
                    <label htmlFor="bonus-points" className="accountability-configure-checkpoint-modal__label">
                      Bonus Points (Target Reached)
                    </label>
                    <TextInput
                      value={form.bonusPoints ?? ''}
                      onChange={({ target }) => handleFormChange('bonusPoints', target.value)}
                      name="bonus-points"
                      disabled={status === ARCHIVED}
                      numberOnly
                    />
                  </div>
                </div>

                <div className="accountability-configure-checkpoint-modal__toggle-container">
                  <label className="accountability-configure-checkpoint-modal__label">Multiple Passes?</label>
                  <div className="accountability-configure-checkpoint-modal__toggle">
                    <button
                      className={getClassNames('accountability-configure-checkpoint-modal__toggle__button', {
                        active: !form.multiplePassesConfigured
                      })}
                      onClick={() => handleFormChange('multiplePassesConfigured', false)}
                      disabled={status === ARCHIVED}
                    >
                      No
                    </button>
                    <button
                      className={getClassNames('accountability-configure-checkpoint-modal__toggle__button', {
                        active: form.multiplePassesConfigured
                      })}
                      onClick={() => handleFormChange('multiplePassesConfigured', true)}
                      disabled={status === ARCHIVED}
                    >
                      Yes
                    </button>
                  </div>
                </div>

                {form.multiplePassesConfigured && (
                  <div className="accountability-configure-checkpoint-modal__input-group">
                    <p>Configure Multiple Passes</p>
                    <div className="accountability-configure-checkpoint-modal__pass-input">
                      <label htmlFor="passes" className="accountability-configure-checkpoint-modal__label">
                        How many Passes?
                      </label>
                      <div>
                        <TextInput
                          value={numberOfPasses}
                          onChange={({ target }) => setNumberOfPasses(target.value)}
                          name="passes"
                          disabled={status === ARCHIVED}
                          numberOnly
                        />
                        <button onClick={handleConfigurePassesClick}>
                          <EditListIcon />
                        </button>
                      </div>
                    </div>
                    <div className="accountability-configure-checkpoint-modal__inline-input">
                      <label
                        htmlFor="single-pass-base-points"
                        className="accountability-configure-checkpoint-modal__label"
                      >
                        Base Points (After Each Pass)
                      </label>
                      <TextInput
                        value={form.multiplePassBasePoints ?? ''}
                        onChange={({ target }) => handleFormChange('multiplePassBasePoints', target.value)}
                        name="single-pass-base-points"
                        disabled={status === ARCHIVED}
                        numberOnly
                      />
                    </div>
                    <div className="accountability-configure-checkpoint-modal__inline-input">
                      <label
                        htmlFor="all-pass-base-points"
                        className="accountability-configure-checkpoint-modal__label"
                      >
                        Base Points (All Passes Completed)
                      </label>
                      <TextInput
                        value={form.multiplePassCompletedBasePoints ?? ''}
                        onChange={({ target }) => handleFormChange('multiplePassCompletedBasePoints', target.value)}
                        name="all-pass-base-points"
                        disabled={status === ARCHIVED}
                        numberOnly
                      />
                    </div>
                    <div className="accountability-configure-checkpoint-modal__inline-input">
                      <label htmlFor="pass-target-time" className="accountability-configure-checkpoint-modal__label">
                        Target Time Per Pass (Minutes)
                      </label>
                      <TextInput
                        value={form.multiplePassTargetTimeInMinutes ?? ''}
                        onChange={({ target }) => handleFormChange('multiplePassTargetTimeInMinutes', target.value)}
                        name="pass-target-time"
                        disabled={status === ARCHIVED}
                        numberOnly
                      />
                    </div>
                    <div className="accountability-configure-checkpoint-modal__inline-input">
                      <label htmlFor="pass-bonus-points" className="accountability-configure-checkpoint-modal__label">
                        Bonus Points (Target Reached)
                      </label>
                      <TextInput
                        value={form.multiplePassBonusPoints ?? ''}
                        onChange={({ target }) => handleFormChange('multiplePassBonusPoints', target.value)}
                        name="pass-bonus-points"
                        disabled={status === ARCHIVED}
                        numberOnly
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="accountability-configure-checkpoint-modal__button-container">
            <Button text={status === ARCHIVED ? 'Close' : 'Cancel'} onClick={closeModal} />
            {status !== ARCHIVED && <Button text="Save" onClick={handleSaveClick} />}
          </div>
        </div>
      </Modal>

      <AccountabilityConfigurePassesModal
        status={status}
        show={showPassesModal}
        close={() => setShowPassesModal(false)}
        passes={form.checkpointPasses}
        savePasses={(passes) => handleFormChange('checkpointPasses', passes)}
      />
    </>
  );
};

export default AccountabilityConfigureCheckpointModal;
